// Sticky Header

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Calls when Component is Initialized in DOM
   *
   */
  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
    this.headerBounds = {};
    this.currentScrollTop = 0;

    this.onScrollHandler = this.onScroll.bind(this);
    this.onClickOutsideHandler = this.handleClickOutside.bind(this);

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('header-sticky');
    };

    window.addEventListener('scroll', this.onScrollHandler, false);
    document.addEventListener('click', this.onClickOutsideHandler, true);

    // Get all sticky announcement bars
    this.announcementBars = document.querySelectorAll('[data-top-bar][data-sticky="true"]');
    this.updateAnnouncementHeight = this.updateAnnouncementHeight.bind(this);

    if (this.announcementBars.length > 0) {
      this.announcementBarHeight = this.calculateTotalAnnouncementHeight();
      this.updateAnnouncementHeight();
      this.announcementResizeObserver = new ResizeObserver(this.updateAnnouncementHeight);
      this.announcementBars.forEach(bar => {
        this.announcementResizeObserver.observe(bar);
      });
    } else {
      this.announcementBarHeight = 0;
      this.setHeaderPosition(0);
    }

    window.addEventListener('resize', this.updateAnnouncementHeight, false);
    this.createObserver();
  }

  /**
   * Calls when Component is removed from DOM
   *
   */
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    document.removeEventListener('click', this.onClickOutsideHandler, true);
    window.removeEventListener('resize', this.updateAnnouncementHeight);
    if (this.announcementResizeObserver) this.announcementResizeObserver.disconnect();
  }

  /**
   * To observe Sticky header visibility on User Scroll
   *
   */
  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  handleClickOutside(event) {
    // If click is outside the header and dropdowns
    const isInsideHeader = this.header.contains(event.target);
    const isInsideDropdown = event.target.closest('.dropdown.open') !== null;

    if (!isInsideHeader && !isInsideDropdown) {
      this.closeMenuDropdowns();
    }
  }

  /**
   * Window Scroll event
   *
   */
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // console.log(scrollTop, this.currentScrollTop, this.headerBounds.bottom);
    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.reveal.bind(this));
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  /**
   * Hide Sticky header
   * Close Menu Drawer / Seach Modal / Cart Drawer
   *
   */
  hide() {
    if (!this.headerIsAlwaysSticky) {
      this.header.classList.add('header-hidden', 'header-sticky');
      this.closeSearchModal();
      this.closeMenuDropdowns();
    }
    // Keep header positioned below the announcement bar while scrolling
    this.setHeaderPosition(this.announcementBarHeight);
  }

  /**
   * Show Sticky Header
   *
   */
  reveal() {
    if (!this.headerIsAlwaysSticky) {
      this.header.classList.add('header-sticky', 'animate');
      this.header.classList.remove('header-hidden');
    }
    this.setHeaderPosition(this.announcementBarHeight);
  }

  reset() {
    // Ensure header remains offset by the announcement bar height
    this.setHeaderPosition(this.announcementBarHeight);
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('header-hidden', 'header-sticky', 'animate');
  }

  /**
   * Set header position with smooth transition
   * @param {number} position - The top position value in pixels
   */
  setHeaderPosition(position) {
    if (!this.header) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // On mobile, keep header at top (0) and push announcement bars below header
      this.header.style.top = `0px`;
      if (this.announcementBars.length > 0) {
        let cumulativeTop = this.header.offsetHeight;
        this.announcementBars.forEach(bar => {
          const sectionId = bar.dataset.sectionId;
          const section = document.getElementById(`shopify-section-${sectionId}`);
          if (section && position > 0) {
            section.style.top = `${cumulativeTop}px`;
            cumulativeTop += bar.offsetHeight;
          } else if (section) {
            section.style.top = `0px`;
          }
        });
      }
    } else {
      // On desktop/tablet, position header below announcement and clear mobile positioning
      if (this.announcementBars.length > 0) {
        this.announcementBars.forEach(bar => {
          const sectionId = bar.dataset.sectionId;
          const section = document.getElementById(`shopify-section-${sectionId}`);
          // Let the announcement bar stacking script handle positioning
        });
      }
      this.header.style.top = `${position}px`;
    }
  }

  /**
   * Calculate total height of all sticky announcement bars
   */
  calculateTotalAnnouncementHeight() {
    let totalHeight = 0;
    this.announcementBars.forEach(bar => {
      totalHeight += bar.offsetHeight || 0;
    });
    return totalHeight;
  }

  /**
   * Recalculate announcement bar height and update header position
   */
  updateAnnouncementHeight() {
    const newHeight = this.announcementBars.length > 0 ? this.calculateTotalAnnouncementHeight() : 0;
    this.announcementBarHeight = newHeight || 0;
    this.setHeaderPosition(this.announcementBarHeight);
  }

  /**
   * Close Search Modal on Window Scroll
   *
   */
  closeSearchModal() {
    let searchModal = document.querySelector('search-modal') || null;
    if (searchModal == null) return;

    if (searchModal.classList.contains('open__modal') && typeof searchModal.close == 'function') searchModal.close(false);
  }

  /**
   * Close Menu Dropdowns
   *
   */
  closeMenuDropdowns() {
    let openDropdowns = Array.from(document.querySelectorAll('.dropdown.open'));
    openDropdowns.forEach((ele) => {
      ele.classList.remove('open');
    });
  }
}
customElements.define('sticky-header', StickyHeader);